import Enumerable from "linq";
import { IItem } from "./IItem";
import { instanceOfIExtraMemberAccessible } from "./IExtraMemberAccessible";
import { IRelational } from "./IRelational";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { ObjectId } from "../ObjectId";

export abstract class AbstractItem implements IItem, IRelational {
  // 動的にクラス名を取るのは不可能.typeKeyは末端クラスで指定するようにする.
  constructor(public readonly typeKey: string) {
  }

  public privateId: number = null;
  public uniqueKey: string = null;
  public id: string = null;
  public sectionObjectUid: string = null;
  public typeDetail: string = null;
  public isEditable: boolean = false;
  public isAdditional: boolean = false;
  public isDeleted: boolean = false;
  public viewTitle: string = null;
  public toolTip: string;
  public actions: RelationalActionParameter[] = [];

  get formatElementName(): string {
    const ids = this.sectionObjectUid
      ? this.sectionObjectUid.split("_")
      : [];
    return ids.length > 1
      ? ids[1]
      : "";
  }

  clone(): IItem {
    const clone = this.createCloneInstance() as AbstractItem;
    this.applyMembers(clone);
    // 外部からの拡張注入実装.
    if (instanceOfIExtraMemberAccessible(this)) {
      this.applyExtraMembers(this, clone);
    }
    return clone;
  }

  protected abstract createCloneInstance(): AbstractItem;

  protected applyMembers(clone: AbstractItem): void {
    clone.privateId = this.cloneValue(this.privateId);
    clone.uniqueKey = this.cloneValue(this.uniqueKey);
    clone.id = this.cloneValue(this.id);
    clone.typeDetail = this.cloneValue(this.typeDetail);
    clone.isEditable = this.cloneValue(this.isEditable);
    clone.isAdditional = this.cloneValue(this.isAdditional);
    clone.isDeleted = this.cloneValue(this.isDeleted);
    clone.viewTitle = this.cloneValue(this.viewTitle);
    clone.toolTip = this.cloneValue(this.toolTip);
    clone.sectionObjectUid = this.cloneValue(this.sectionObjectUid);
    clone.actions = this.cloneValue(this.actions);
  }

  protected cloneValue<T>(value: T): T {
    return ((value !== undefined) && (value !== null))
      ? (Array.isArray(value) ? value.concat() : ((value as any).clone ? (value as any).clone() : value))
      : value;
  }

  initialize(
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem[]
  ): IItem {
    // 空実装で実装しておく.必要なItemでoverrideする事.
    return null;
  }

  handleItemUpdated(
    originalItem: IItem,
    updatedItem: IItem,
    isInSameSection: boolean,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    // 空実装で実装しておく.必要なItemでoverrideする事.
    return null;
  }

  handleOwnUpdated(
    originalItem: IItem,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    // 空実装で実装しておく.必要なItemでoverrideする事.
    return null;
  }

  getUpdatedClone<T extends AbstractItem>(...updates: ((s: T) => T)[]): T {
    return Enumerable.from(updates)
      .where(u => !!u)
      .aggregate(null, (source: T, update: (s: T) => T): T => {
        const target = source
          ? source
          : this;
        const result = update(target as T);
        return result
          ? result
          : source;
      });
  }

  createUpdatedClone<T extends AbstractItem>(update: (s: T) => void): T {
    const clone = this.clone() as T;
    update(clone);
    return clone;
  } 
}
